import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import OpportunityChart from './OpportunityChart';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { decodeId } from '../utils/urlSecurity';
import apiService from '../services/apiService';
// Using OpportunityChart component for consistency with admin interface

const Results = () => {
  const { submissionId: encodedSubmissionId } = useParams();
  const location = useLocation();
  const [surveyData, setSurveyData] = useState(location.state?.surveyData || null);
  const [formData, setFormData] = useState(location.state?.formData || null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Decode the submission ID from the URL parameter
  const submissionId = decodeId(encodedSubmissionId);

  // Strategic category definitions and colors based on median calculation
  const categoryInfo = {
    'Quick Wins': {
      description: 'tech_score > tech_median AND roi_score > roi_median - prioritize for immediate implementation',
      color: '#36b37e',
      backgroundColor: 'rgba(54, 179, 126, 0.6)'
    },
    'Strategic Challenges': {
      description: 'tech_score <= tech_median AND roi_score > roi_median - valuable but technically challenging',
      color: '#ff5630',
      backgroundColor: 'rgba(255, 86, 48, 0.6)'
    },
    'Technical Experiments': {
      description: 'tech_score > tech_median AND roi_score <= roi_median - pilot programs recommended',
      color: '#0065ff',
      backgroundColor: 'rgba(0, 101, 255, 0.6)'
    },
    'Lower Priority': {
      description: 'tech_score <= tech_median AND roi_score <= roi_median - consider for future phases',
      color: '#6b778c',
      backgroundColor: 'rgba(107, 119, 140, 0.6)'
    }
  };

  // Fetch data from API if not available in state
  useEffect(() => {
    const fetchResultsData = async () => {
      if (!surveyData && submissionId) {
        setLoading(true);
        setError(null);
        
        try {
          const data = await apiService.getResults(encodedSubmissionId);
          
          // Set survey data from API response
          setSurveyData({
            tech_feasibility_score: data.tech_feasibility_score,
            roi_potential_score: data.roi_potential_score,
            strategic_value_score: data.strategic_value_score,
            strategic_category: data.strategic_category,
            chart_data: data.chart_data
          });
          
          // Set form data from API response
          setFormData({
            opportunity_name: data.opportunity_name,
            opportunity_description: data.opportunity_description,
            email: 'User' // Don't expose email for privacy when accessed via direct link
          });
          
        } catch (err) {
          console.error('Error fetching results data:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchResultsData();
  }, [encodedSubmissionId, submissionId, surveyData]);

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Loading Results...</h2>
          <p>Please wait while we load your assessment results.</p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3', 
              borderTop: '4px solid #0066cc', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <h2>Error Loading Results</h2>
          <p style={{ color: '#dc3545', marginBottom: '20px' }}>{error}</p>
          <div>
            <Link to="/" className="btn btn-primary" style={{ marginRight: '10px' }}>
              Take New Assessment
            </Link>
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div className="container">
        <div className="card">
          <h2>No Data Available</h2>
          <p>No assessment data found. Please take a new assessment.</p>
          <Link to="/" className="btn btn-primary">
            Start Assessment
          </Link>
        </div>
      </div>
    );
  }

  // Using OpportunityChart component for professional visualization

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('AI Opportunity Assessment Report', pageWidth / 2, 20, { align: 'center' });
      
      // Opportunity details
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Opportunity: ${formData?.opportunity_name || 'N/A'}`, 20, 40);
      pdf.text(`Email: ${formData?.email || 'N/A'}`, 20, 50);
      pdf.text(`Assessment Date: ${new Date().toLocaleDateString()}`, 20, 60);
      
      // Scores section
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text('Assessment Scores', 20, 80);
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Technical Feasibility: ${surveyData.tech_feasibility_score}/10`, 30, 95);
      pdf.text(`ROI Potential: ${surveyData.roi_potential_score}/10`, 30, 105);
      pdf.text(`Strategic Value: ${surveyData.strategic_value_score}/10`, 30, 115);
      pdf.text(`Strategic Category: ${surveyData.strategic_category}`, 30, 125);
      
      // Category description
      const categoryDesc = categoryInfo[surveyData.strategic_category]?.description || '';
      pdf.text(`Category Description: ${categoryDesc}`, 20, 140, { maxWidth: pageWidth - 40 });
      
      // Capture chart
      const chartElement = document.getElementById('assessment-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement, {
          backgroundColor: '#ffffff',
          scale: 2
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 40;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 20, 160, imgWidth, imgHeight);
      }
      
      // Save PDF
      pdf.save(`AI_Assessment_${formData?.opportunity_name?.replace(/\s+/g, '_') || 'Report'}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Assessment Results</h1>
        <p>Your AI Opportunity Analysis</p>
      </div>
      
      {/* Chart Visualization */}
      <div className="card">
        <h2>Assessment Matrix</h2>
        <div 
          id="assessment-chart"
          style={{ 
            minHeight: '500px', 
            position: 'relative',
            backgroundColor: '#ffffff',
            padding: '20px',
            border: '1px solid #e1e5e9',
            borderRadius: '8px',
            marginBottom: '20px',
            overflow: 'visible'
          }}
        >
          {submissionId && surveyData && (
            <OpportunityChart 
              key={`chart-${submissionId}`}
              submissionId={parseInt(submissionId)} 
              isPortfolio={false}
              isPublic={true}
              preloadedData={surveyData.chart_data}
              isResultsView={true}
            />
          )}
        </div>
      </div>
      
      {/* Detailed Assessment Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        
        {/* Tech Feasibility Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #36b37e' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#36b37e', margin: 0 }}>Tech Feasibility Assessment</h3>
            <span style={{ 
              backgroundColor: '#36b37e', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              +{surveyData.tech_feasibility_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>Predictable patterns in process flow (~5)</li>
            <li>Cloud services acceptable for implementation (~4)</li>
            <li>APIs available for required systems (+6)</li>
            <li>Well-documented requirements</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: 'rgba(54, 179, 126, 0.15)', 
            borderRadius: '6px',
            borderLeft: '4px solid #36b37e'
          }}>
            <strong>Rationale:</strong> High automation potential with structured templates and established data sources.
          </div>
        </div>

        {/* ROI Potential Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #ff5630' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#ff5630', margin: 0 }}>ROI Potential</h3>
            <span style={{ 
              backgroundColor: '#ff5630', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              +{surveyData.roi_potential_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>High daily transaction volume</li>
            <li>25% time savings expected</li>
            <li>Moderate change management needed</li>
            <li>Significant risk reduction potential (~9)</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: 'rgba(255, 86, 48, 0.15)', 
            borderRadius: '6px',
            borderLeft: '4px solid #ff5630'
          }}>
            <strong>Rationale:</strong> Significant cost savings from automation and substantial risk reduction.
          </div>
        </div>

        {/* Strategic Value Assessment Card */}
        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '2px solid #0065ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#0065ff', margin: 0 }}>Strategic Value</h3>
            <span style={{ 
              backgroundColor: '#0065ff', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginLeft: 'auto'
            }}>
              {surveyData.strategic_value_score}
            </span>
          </div>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>Direct alignment with regulatory directives</li>
            <li>Major brand protection from compliance</li>
            <li>Core operational risk management</li>
          </ul>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: 'rgba(0, 101, 255, 0.15)', 
            borderRadius: '6px',
            borderLeft: '4px solid #0065ff'
          }}>
            <strong>Rationale:</strong> Mission-critical for regulatory standing and operational excellence.
          </div>
        </div>
      </div>

      {/* Assessment Summary and Category */}
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', alignItems: 'start' }}>
          <div>
            <h3>Assessment Scores</h3>
            <p><strong>Technical Feasibility:</strong> {surveyData.tech_feasibility_score}/10</p>
            <p><strong>ROI Potential:</strong> {surveyData.roi_potential_score}/10</p>
            <p><strong>Strategic Value:</strong> {surveyData.strategic_value_score}/10</p>
            <p><strong>Strategic Category:</strong> 
              <span 
                style={{ 
                  color: categoryInfo[surveyData.strategic_category]?.color || '#333',
                  fontWeight: 'bold',
                  marginLeft: '5px'
                }}
              >
                {surveyData.strategic_category}
              </span>
            </p>
          </div>
          <div>
            <h3>Category Analysis</h3>
            <div 
              style={{
                padding: '15px',
                backgroundColor: categoryInfo[surveyData.strategic_category]?.backgroundColor || 'rgba(102, 126, 234, 0.1)',
                border: `2px solid ${categoryInfo[surveyData.strategic_category]?.color || '#667eea'}`,
                borderRadius: '8px'
              }}
            >
              <h4 style={{ 
                color: categoryInfo[surveyData.strategic_category]?.color || '#667eea',
                margin: '0 0 10px 0' 
              }}>
                {surveyData.strategic_category}
              </h4>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {categoryInfo[surveyData.strategic_category]?.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            style={{
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px',
              cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
              minWidth: '200px',
              minHeight: '60px',
              opacity: isGeneratingPDF ? 0.6 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              if (!isGeneratingPDF) {
                e.target.style.backgroundColor = '#5b21b6';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseOut={(e) => {
              if (!isGeneratingPDF) {
                e.target.style.backgroundColor = '#6366f1';
                e.target.style.transform = 'translateY(0px)';
                e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF Report'}
          </button>

          <Link 
            to="/"
            style={{
              backgroundColor: 'white',
              color: '#6366f1',
              border: '2px solid #6366f1',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px',
              textDecoration: 'none',
              minWidth: '200px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#6366f1';
              e.target.style.color = 'white';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#6366f1';
              e.target.style.transform = 'translateY(0px)';
              e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            Take New Assessment
          </Link>
        </div>
        
        <div style={{ marginTop: '20px', color: '#6b7280' }}>
          <p>Need help interpreting your results? Contact our AI assessment team for personalized guidance.</p>
        </div>
      </div>

    </div>
  );
};

export default Results;